import { downloadStationVideo } from '@app/core/media-export/download-station-video';
import { usePlaybackCurrentTs } from '@app/core/playback/hooks/usePlayback';
import { isVideoStation } from '@app/core/stations/helpers';
import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import {
  copyToClipboard,
  DateTimeStr,
  encodeQuickShareHash,
  getQuickShareUrl,
  toDateTimeStr,
} from '@mi-front/core';
import { notifyInfo, TranslateRawFn } from '@mi-front/ui';
import { useCallback } from 'react';
import { useCursor } from '../../stores/stream-screen.store';
import { useStreamSelection } from '../../stores/useStreamSelection';

interface CopyQuickShareParams {
  $t: TranslateRawFn;
  startTime: DateTimeStr;
  endTime: DateTimeStr;
  title?: string;
}

/**
 * Hook to download selection video as TS
 */
export function useDownloadSelectionVideoTs() {
  const station = useActiveStation();
  const { selectionRange, selectionExists } = useStreamSelection();

  return useCallback(() => {
    if (!selectionExists) {
      return;
    }

    if (!isVideoStation(station)) {
      return;
    }

    const { from, to } = selectionRange;

    downloadStationVideo({
      stationId: station.id,
      from: toDateTimeStr(from),
      to: toDateTimeStr(to),
      downloadTs: true,
    });
  }, [station, selectionRange, selectionExists]);
}

/**
 * Hook to set selection start from cursor
 */
export function useSetSelectionStartFromCursor() {
  const cursorTs = useCursor();
  const { setSelectionStart, canStartSelection } = useStreamSelection();

  return useCallback(() => {
    if (!cursorTs || !canStartSelection(cursorTs)) {
      return;
    }

    setSelectionStart(cursorTs);
  }, [cursorTs, setSelectionStart, canStartSelection]);
}

/**
 * Hook to set selection end from cursor
 */
export function useSetSelectionEndFromCursor() {
  const cursorTs = useCursor();
  const { setSelectionEnd, canEndSelection } = useStreamSelection();

  return useCallback(() => {
    if (!cursorTs || !canEndSelection(cursorTs)) {
      return;
    }

    setSelectionEnd(cursorTs);
  }, [cursorTs, setSelectionEnd, canEndSelection]);
}

/**
 * Hook to set selection start from player timestamp
 */
export function useSetSelectionStartFromTs() {
  const currentTs = usePlaybackCurrentTs();
  const { setSelectionStart, canStartSelection } = useStreamSelection();

  return useCallback(() => {
    if (!currentTs || !canStartSelection(currentTs)) {
      return;
    }

    setSelectionStart(currentTs);
  }, [currentTs, setSelectionStart, canStartSelection]);
}

/**
 * Hook to set selection end from player timestamp
 */
export function useSetSelectionEndFromTs() {
  const currentTs = usePlaybackCurrentTs();
  const { setSelectionEnd, canEndSelection } = useStreamSelection();

  return useCallback(() => {
    if (!currentTs || !canEndSelection(currentTs)) {
      return;
    }

    setSelectionEnd(currentTs);
  }, [currentTs, setSelectionEnd, canEndSelection]);
}

/**
 * Hook to copy quick share URL from selection
 */
export function useCopyQuickShareURLFromSelection() {
  const station = useActiveStation();
  const { selectionRange, selectionExists } = useStreamSelection();

  return useCallback(
    ($t: TranslateRawFn) => {
      if (!selectionExists) {
        return;
      }

      const { from, to } = selectionRange;

      const hash = encodeQuickShareHash({
        stationId: station.slug,
        from: toDateTimeStr(from),
        to: toDateTimeStr(to),
      });

      const url = getQuickShareUrl(hash);
      copyToClipboard(url);

      // TODO: refactor into notification center
      notifyInfo(
        $t({
          id: 'logger.streamScreen.selectionMenu.copyQuickShareDone',
          defaultMessage: 'URL Quick Share copiada',
        }),
      );
    },
    [station, selectionRange, selectionExists],
  );
}

/**
 * Hook to copy quick share URL from parameters
 */
export function useCopyQuickShareURLFromParams() {
  const station = useActiveStation();

  return useCallback(
    ({ $t, startTime, endTime, title }: CopyQuickShareParams) => {
      const hash = encodeQuickShareHash({
        stationId: station.slug,
        from: startTime,
        to: endTime,
        title,
      });

      const url = getQuickShareUrl(hash);
      copyToClipboard(url);

      // TODO: refactor into notification center
      notifyInfo(
        $t({
          id: 'logger.streamScreen.selectionMenu.copyQuickShareDone',
          defaultMessage: 'URL Quick Share copiada',
        }),
      );
    },
    [station],
  );
}
